/**
 * [M20] A workspace can be deleted while a member has it open (spec §13.10): their sessions are
 * disconnected and no new sync token is issued. This guard notices — the workspace document's room is
 * refused, so the workspace is asked for again, and a 404 means it is gone — and replaces whatever page
 * was showing with a plain notice, instead of leaving an editor that can no longer save.
 */
import { Link, useParams } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { ApiError } from "../api/client";
import { qk, useWorkspace } from "../api/hooks";
import { useT } from "../i18n";
import { useDoc, useSyncStatus } from "../sync/hooks";

export function WorkspaceGuard({ children }: { children: React.ReactNode }) {
  const { workspaceId } = useParams({ strict: false }) as { workspaceId?: string };
  if (!workspaceId) return <>{children}</>;
  return <Guarded workspaceId={workspaceId}>{children}</Guarded>;
}

function Guarded({ workspaceId, children }: { workspaceId: string; children: React.ReactNode }) {
  const t = useT();
  const qc = useQueryClient();
  const ws = useWorkspace(workspaceId);
  const status = useSyncStatus(useDoc("workspace", workspaceId));
  useEffect(() => {
    if (status === "forbidden") void qc.invalidateQueries({ queryKey: qk.workspace(workspaceId) });
  }, [status, qc, workspaceId]);

  if (ws.error instanceof ApiError && ws.error.status === 404) {
    return (
      <div className="mx-auto w-full max-w-xl px-9 pt-16 text-center" data-testid="workspace-gone">
        <h1 className="mb-2 text-[22px] font-semibold">{t("workspace.gone.title")}</h1>
        <p className="mb-6 text-sm text-neutral-500">{t("workspace.gone.body")}</p>
        <Link to="/" className="text-sm font-medium text-accent hover:underline">
          {t("workspace.gone.home")}
        </Link>
      </div>
    );
  }
  return <>{children}</>;
}
